import { EarningsInsightType } from '@api/api/earnings-insights';
import { KgInsight } from '@api/api/kg-insights';
import { getTimeTillEndOfDayMs } from '@helpers/utils/dates/getTimeTillEndOfDayMs';
import { useQueries } from '@tanstack/react-query';
import {
  useEarningsInsightPreviews,
  useEarningsInsightPreviewsV4,
} from '@terminal/react-query-hooks/insights/useGetEarningsInsightPreview';
import {
  useEarningsInsightRecaps,
  useEarningsInsightRecapsV4,
} from '@terminal/react-query-hooks/insights/useGetEarningsInsightRecap';
import { QUERY_KEY_KG_INSIGHT } from '@terminal/react-query-hooks/insights/useKgInsight';
import { useThemesDetails } from '@terminal/react-query-hooks/knowledge-graph/useThemesDetails';
import { getKgInsight } from '@terminal/services/insights/kg-insights-service';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useMemo } from 'react';

import type { EarningsTopic } from './earnings-reporting-summary/types';
import type { ThemeEarningsInsightsForEarnings } from './useThemeEarningsInsights';
import {
  getThemesToWatchByEarningsId,
  type InsightThemesSource,
  mapKgInsightToThemesPayload,
} from './utils';

export type ThemeEarningsThemesByEarningsId = Map<string, EarningsTopic[]>;

const useKgInsightsById = (insightIds: string[]) =>
  useQueries({
    queries: insightIds.map(insightId => ({
      queryKey: [QUERY_KEY_KG_INSIGHT, insightId],
      queryFn: () => getKgInsight(insightId),
      staleTime: getTimeTillEndOfDayMs(),
      enabled: !!insightId,
    })),
    combine: results => ({
      data: results
        .map(result => result.data)
        .filter((data): data is KgInsight => data != null),
      isFetching: results.some(result => result.isFetching),
      failedInsightIds: results.flatMap((result, index) =>
        result.isError ? [insightIds[index]] : []
      ),
    }),
  });

export const useThemeEarningsThemes = (
  earningsInsightByEarningsId: Map<string, ThemeEarningsInsightsForEarnings>,
  insightType: EarningsInsightType,
  isEnabled = true
) => {
  const isEarningsInsightsV4Enabled = useFlags(state =>
    state.getFeatureFlagState('REASON-2467-earnings-insights-v4')
  );
  const selectedInsights = useMemo(
    () =>
      [...earningsInsightByEarningsId.values()].flatMap(insights => {
        const insight = insights[insightType];
        return insight ? [insight] : [];
      }),
    [earningsInsightByEarningsId, insightType]
  );

  const earningsInsightIds = useMemo(
    () =>
      isEnabled
        ? selectedInsights
            .filter(insight => insight.source === 'earnings-insight')
            .map(insight => insight.id)
        : [],
    [isEnabled, selectedInsights]
  );

  const kgInsightIds = useMemo(
    () =>
      isEnabled
        ? selectedInsights
            .filter(insight => insight.source === 'kg-insight')
            .map(insight => insight.id)
        : [],
    [isEnabled, selectedInsights]
  );

  const {
    data: kgInsights,
    isFetching: isKgFetching,
    failedInsightIds,
  } = useKgInsightsById(kgInsightIds);

  const fallbackInsightIds = selectedInsights.flatMap(insight =>
    insight.fallbackEarningsInsightId && failedInsightIds.includes(insight.id)
      ? [insight.fallbackEarningsInsightId]
      : []
  );

  const insightIdsToFetch = [...earningsInsightIds, ...fallbackInsightIds];

  const recapIds = insightType === 'recap' ? insightIdsToFetch : [];
  const previewIds = insightType === 'preview' ? insightIdsToFetch : [];
  const legacyRecaps = useEarningsInsightRecaps(
    isEarningsInsightsV4Enabled ? [] : recapIds
  );
  const v4Recaps = useEarningsInsightRecapsV4(
    isEarningsInsightsV4Enabled ? recapIds : []
  );
  const legacyPreviews = useEarningsInsightPreviews(
    isEarningsInsightsV4Enabled ? [] : previewIds
  );
  const v4Previews = useEarningsInsightPreviewsV4(
    isEarningsInsightsV4Enabled ? previewIds : []
  );
  const { data: recaps, isFetching: isRecapsFetching } =
    isEarningsInsightsV4Enabled ? v4Recaps : legacyRecaps;
  const { data: previews, isFetching: isPreviewsFetching } =
    isEarningsInsightsV4Enabled ? v4Previews : legacyPreviews;
  const earningsDetails = insightType === 'recap' ? recaps : previews;

  const kgPayloads = useMemo(
    () => kgInsights.map(mapKgInsightToThemesPayload),
    [kgInsights]
  );

  const themeSources = useMemo(() => {
    const byEarningsId = new Map<string, InsightThemesSource>(
      earningsDetails.map(detail => [
        detail.earnings_id,
        {
          earnings_id: detail.earnings_id,
          themes: detail.themes,
          other_themes:
            'other_themes' in detail ? detail.other_themes : undefined,
        },
      ])
    );
    for (const payload of kgPayloads) {
      byEarningsId.set(payload.earnings_id, payload);
    }
    return [...byEarningsId.values()];
  }, [earningsDetails, kgPayloads]);

  const themeIds = useMemo(
    () =>
      themeSources.flatMap(source =>
        source.themes.map(theme => theme.theme_id)
      ),
    [themeSources]
  );

  const { data: themes, isLoading: isThemesLoading } = useThemesDetails({
    themeIds,
  });

  const themesById = useMemo(
    () => new Map(themes.map(theme => [theme.id, theme])),
    [themes]
  );

  const themesByEarningsId: ThemeEarningsThemesByEarningsId = useMemo(
    () =>
      getThemesToWatchByEarningsId({
        insights: themeSources,
        themesById,
        themeIdsCount: themeIds.length,
        isThemesLoading,
      }),
    [themeSources, themesById, themeIds.length, isThemesLoading]
  );

  return {
    themesByEarningsId,
    isFetching:
      isKgFetching || isRecapsFetching || isPreviewsFetching || isThemesLoading,
  };
};
